import React, { Suspense, lazy } from "react";

export const BADGE_TYPES = {
  MY_HR_MENU: "myHrMenu",
  MY_LEAVE_MENU: "myLeaveMenu",
  MY_LEAVE_ADMIN_MENU: "myLeaveAdminMenu",
  MY_TIME_MENU: "myTimeMenu",
  MY_TIMESHEET_MENU: "myTimesheetMenu",
} as const;

export type BadgeType = (typeof BADGE_TYPES)[keyof typeof BADGE_TYPES];

// Lazy load badge components to avoid circular dependencies
const LazyMyHrMenuBadges = lazy(() =>
  import("my-hr/components/menu-badges").then((m) => ({
    default: m.MyHrMenuBadges,
  })),
);
const LazyMyLeaveMenuBadges = lazy(() =>
  import("my-leave/components/menu-badges").then((m) => ({
    default: m.MyLeaveMenuBadges,
  })),
);
const LazyMyLeaveMenuAdminBadges = lazy(() =>
  import("my-leave/components/menu-admin-badges").then((m) => ({
    default: m.MyLeaveMenuAdminBadges,
  })),
);
const LazyMyTimeMenuBadges = lazy(() =>
  import("my-time/components/menu-badges").then((m) => ({
    default: m.MyTimeMenuBadges,
  })),
);
const LazyMyTimesheetMenuBadge = lazy(() =>
  import("my-timesheet/components/menu-badge").then((m) => ({
    default: m.MyTimesheetMenuBadge,
  })),
);

const badgeComponents: Record<BadgeType, React.LazyExoticComponent<React.FC>> =
  {
    [BADGE_TYPES.MY_HR_MENU]: LazyMyHrMenuBadges,
    [BADGE_TYPES.MY_LEAVE_MENU]: LazyMyLeaveMenuBadges,
    [BADGE_TYPES.MY_LEAVE_ADMIN_MENU]: LazyMyLeaveMenuAdminBadges,
    [BADGE_TYPES.MY_TIME_MENU]: LazyMyTimeMenuBadges,
    [BADGE_TYPES.MY_TIMESHEET_MENU]: LazyMyTimesheetMenuBadge,
  };

/**
 * Renders a badge component by its type identifier.
 * Returns null if badgeType is undefined or not found in registry.
 */
export const renderBadge = (
  badgeType: BadgeType | undefined,
): React.ReactNode => {
  if (!badgeType) return null;
  const BadgeComponent = badgeComponents[badgeType];
  return BadgeComponent ? (
    <Suspense fallback={null}>
      <BadgeComponent />
    </Suspense>
  ) : null;
};
